// The SCSS module import is commented out because we're not using a bundler that
// supports CSS-in-JS modules in Drupal. Provide JS fallbacks for the variables
// that the rest of this file expects. If CSS custom properties are present, we
// read them, otherwise fall back to hard-coded pixels.

const readCssNumber = (prop, fallback) => {
  const val = getComputedStyle(document.documentElement).getPropertyValue(prop);
  const num = parseInt(val, 10);
  return Number.isNaN(num) ? fallback : num;
};

const variables = {
  breakpointMenu: readCssNumber("--breakpoint-menu", 992), // Bootstrap lg default
  navbarHeightDesktop: readCssNumber("--navbar-height-desktop", 85),
  navbarHeightMobile: readCssNumber("--navbar-height-mobile", 85),
};

const initToggleNavOnScroll = () => {
  let prevScrollPos;
  const navbarWrapper = document.querySelector("nav.navbar");
  // Sticky logo elements (desktop only)
  const originalLogo = document.querySelector(".navbar-brand .site-logo");
  let stickyLogoWrapper = null;

  const ensureStickyLogo = () => {
    if (!originalLogo || stickyLogoWrapper) return;

    // Create wrapper
    stickyLogoWrapper = document.createElement("div");
    stickyLogoWrapper.id = "stickyLogo";
    stickyLogoWrapper.className = "position-fixed d-none";
    stickyLogoWrapper.style.top = "0";
    stickyLogoWrapper.style.zIndex = "1031";

    // Clone logo link (deep clone to include img)
    const logoClone = originalLogo.cloneNode(true);
    // Make sure it fills the wrapper and is right-aligned
    logoClone.classList.add("d-block", "ms-auto");

    stickyLogoWrapper.appendChild(logoClone);
    document.body.appendChild(stickyLogoWrapper);
  };

  const updateStickyLogoPosition = () => {
    if (!stickyLogoWrapper || !navbarWrapper) return;

    const container = navbarWrapper.querySelector(".container");
    if (!container) return;

    // Width equals original logo width
    const logoWidth = originalLogo.getBoundingClientRect().width;
    stickyLogoWrapper.style.width = `${logoWidth}px`;

    // Align wrapper to the right edge of the container
    const rect = container.getBoundingClientRect();
    const left = rect.right - logoWidth;
    stickyLogoWrapper.style.left = `${left}px`;
  };

  const toggleStickyLogo = (show) => {
    if (!stickyLogoWrapper) return;
    if (show) {
      stickyLogoWrapper.classList.remove("d-none");
      updateStickyLogoPosition();
    } else {
      stickyLogoWrapper.classList.add("d-none");
    }
  };

  ensureStickyLogo();
  window.addEventListener("resize", updateStickyLogoPosition);

  if (navbarWrapper) {
    const NAVBAR_VISIBLE = "visible";
    const NAVBAR_HIDE_ON_SCROLL = "hide-on-scroll";
    let navToggleTrigger = 85; // value in px

    const handleNavigationHide = () => {
      const currentScrollPos =
        Math.sign(window.scrollY) === -1 ? 0 : window.scrollY;
      const isOpen = document.body.classList.contains("menu-opened");
      navToggleTrigger =
        window.innerWidth > parseInt(variables.breakpointMenu, 10)
          ? parseInt(variables.navbarHeightDesktop)
          : parseInt(variables.navbarHeightMobile);

      if (navToggleTrigger > currentScrollPos) {
        navbarWrapper.classList.add(NAVBAR_VISIBLE);
        // Show full navbar, hide sticky logo
        toggleStickyLogo(false);
      } else if (currentScrollPos > prevScrollPos && !isOpen) {
        // isOpen fix is needed, if the scroll position is changed during toggled burger menu
        navbarWrapper.classList.remove(NAVBAR_VISIBLE);
        // Hide navbar, show sticky logo on desktop only
        if (window.innerWidth >= parseInt(variables.breakpointMenu, 10)) {
          toggleStickyLogo(true);
        }
      } else {
        navbarWrapper.classList.add(NAVBAR_VISIBLE);
        toggleStickyLogo(false);
      }

      prevScrollPos = currentScrollPos;
    };

    if (navbarWrapper.classList.contains(NAVBAR_HIDE_ON_SCROLL)) {
      window.addEventListener("scroll", handleNavigationHide);
      handleNavigationHide();
    }
  }
};

const provideViewportHeight = () => {
  // Provide viewport height as css variable
  const updateViewportHeight = () => {
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
  window.addEventListener("resize", updateViewportHeight);
  updateViewportHeight();
};

const provideHeaderHeights = () => {
  const root = document.documentElement;
  // Keep backward compatibility for markup with and without role="banner".
  const header = document.querySelector(
    'header[role="banner"], .layout-container > header.bg-primary.text-white, .layout-container > header'
  );
  const navbar = document.querySelector("nav.navbar");

  const updateHeaderHeights = () => {
    const headerHeight = header
      ? Math.round(header.getBoundingClientRect().height)
      : 0;
    const navbarHeight = navbar
      ? Math.round(navbar.getBoundingClientRect().height)
      : 0;

    root.style.setProperty("--header-banner-height", `${headerHeight}px`);
    root.style.setProperty("--navbar-height", `${navbarHeight}px`);
  };

  updateHeaderHeights();
  window.addEventListener("resize", updateHeaderHeights);

  if (window.ResizeObserver) {
    const observer = new ResizeObserver(updateHeaderHeights);
    if (header) {
      observer.observe(header);
    }
    if (navbar) {
      observer.observe(navbar);
    }
  }
};

// check navigator platform + workaround for iOS 13
const iOS = () => {
  return (
    [
      "iPad Simulator",
      "iPhone Simulator",
      "iPod Simulator",
      "iPad",
      "iPhone",
      "iPod",
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

const getDropdownController = (toggle) => {
  if (!toggle) {
    return null;
  }

  // Prefer native Bootstrap 5 API to avoid hard jQuery dependency.
  if (window.bootstrap?.Dropdown) {
    return window.bootstrap.Dropdown.getOrCreateInstance(toggle);
  }

  // Keep backward compatibility if jQuery plugin is present.
  if (window.jQuery && typeof window.jQuery(toggle).dropdown === "function") {
    return {
      show: () => window.jQuery(toggle).dropdown("show"),
      hide: () => window.jQuery(toggle).dropdown("hide"),
    };
  }

  return null;
};

const setDropdownState = (toggle, state) => {
  const dropdown = getDropdownController(toggle);
  if (!dropdown) return;
  if (state) {
    dropdown.show();
  } else {
    dropdown.hide();
  }
  toggle.blur();
};

const toggleDropdown = (element, state) => {
  setDropdownState(element.querySelector(".dropdown-toggle"), state);
};

const canHover = () => {
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
};

const isMobileMenu = () => {
  return window.innerWidth < variables.breakpointMenu;
};

const initNavHover = () => {
  let timer = null;
  let lastHover = null;

  const showDropdown = (element) => {
    if (lastHover) {
      toggleDropdown(lastHover, false);
      lastHover = null;
    }
    if (element) {
      toggleDropdown(element, true);
    }
    lastHover = element;
  };
  document.querySelectorAll("nav.navbar .nav-item.dropdown").forEach((el) => {
    el.addEventListener("mouseover", () => {
      if (canHover() && !isMobileMenu()) {
        showDropdown(el);
        clearTimeout(timer);
      }
    });
    el.addEventListener("mouseout", () => {
      timer = setTimeout(() => showDropdown(null), 1000);
    });
  });
};

const initMenuOpenBodyClass = (navbar) => {
  let scrollPosition = 0;

  navbar.addEventListener("show.bs.collapse", () => {
    // we need a slight offset, as "shown" fires a bit too late and show too early (before display none gets removed)
    setTimeout(() => {
      const body = document.body;
      body.classList.add("menu-opened");

      // if user has iOS device, we have to use a workaround, as overflow hidden gets ignored
      if (iOS()) {
        scrollPosition = window.scrollY;
        body.style.overflow = "hidden";
        body.style.position = "fixed";
        body.style.top = `-${scrollPosition}px`;
        body.style.width = "100%";
      }
    }, 50);
  });

  navbar.addEventListener("hide.bs.collapse", () => {
    const body = document.body;
    body.classList.remove("menu-opened");

    if (iOS()) {
      body.style.removeProperty("overflow");
      body.style.removeProperty("position");
      body.style.removeProperty("top");
      body.style.removeProperty("width");
      window.scrollTo({
        top: scrollPosition,
        left: 0,
        behavior: "instant",
      });
    }
  });
};

const initMobileMenuActiveDropdown = (navbar) => {
  const activeNavItems = document.querySelectorAll(
    "#navbarMainContent .nav-item.dropdown.active"
  );
  const showActiveMenu = () => {
    setTimeout(() => {
      activeNavItems.forEach((navItem) => {
        toggleDropdown(navItem, true);
      });
    }, 0);
  };

  navbar.addEventListener("show.bs.collapse", showActiveMenu);
};

const initOverlayToggle = (navbar) => {
  const overlay = document.querySelector("#menuOverlay");
  const toggler = document.querySelector("#navbar-toggler");
  if (!overlay || !toggler) return;
  overlay.addEventListener("click", () => {
    if (navbar.classList.contains("show")) {
      toggler.click();
    }
  });

  navbar.addEventListener("show.bs.collapse", function () {
    overlay.classList.toggle("z-index-n1");
    overlay.classList.toggle("z-index-1");
  });

  navbar.addEventListener("hide.bs.collapse", function () {
    overlay.classList.toggle("z-index-n1");
    overlay.classList.toggle("z-index-1");
  });
};

const initMobileAccordion = (navbar) => {
  const closeSiblings = (currentItem) => {
    document
      .querySelectorAll("#navbarMainContent .nav-item.dropdown")
      .forEach((item) => {
        if (item !== currentItem) {
          const toggle = item.querySelector(".dropdown-toggle");
          if (toggle) {
            setDropdownState(toggle, false);
            toggle.classList.remove("is-open");
          }
        }
      });
  };

  document
    .querySelectorAll(
      "#navbarMainContent .nav-item.dropdown > .dropdown-toggle"
    )
    .forEach((toggle) => {
      toggle.addEventListener("click", (e) => {
        if (isMobileMenu()) {
          e.preventDefault();
          e.stopPropagation();
          const item = toggle.closest(".nav-item.dropdown");
          const ariaExpanded = toggle.getAttribute("aria-expanded") === "true";
          const isOpen = ariaExpanded || toggle.classList.contains("is-open");
          closeSiblings(item);
          if (isOpen) {
            setDropdownState(toggle, false);
            toggle.classList.remove("is-open");
            toggle.setAttribute("aria-expanded", "false");
          } else {
            setDropdownState(toggle, true);
            toggle.classList.add("is-open");
            toggle.setAttribute("aria-expanded", "true");
          }
        }
      });
    });

  // Also close submenus when clicking any link inside a dropdown on mobile
  document
    .querySelectorAll("#navbarMainContent .dropdown-menu a")
    .forEach((link) => {
      link.addEventListener("click", () => {
        if (isMobileMenu()) {
          const item = link.closest(".nav-item.dropdown");
          if (item) {
            const toggle = item.querySelector(".dropdown-toggle");
            if (toggle) {
              setDropdownState(toggle, false);
              toggle.classList.remove("is-open");
              toggle.setAttribute("aria-expanded", "false");
            }
          }
        }
      });
    });
};

const initCaretStateSync = (navbar) => {
  // Keep caret rotation in sync even when Bootstrap toggles via keyboard, etc.
  navbar.addEventListener("show.bs.dropdown", (e) => {
    const toggle = e.target;
    if (
      toggle &&
      toggle.classList &&
      toggle.classList.contains("dropdown-toggle")
    ) {
      toggle.classList.add("is-open");
    }
  });
  navbar.addEventListener("hide.bs.dropdown", (e) => {
    const toggle = e.target;
    if (
      toggle &&
      toggle.classList &&
      toggle.classList.contains("dropdown-toggle")
    ) {
      toggle.classList.remove("is-open");
    }
  });
};

export function initNav() {
  provideViewportHeight();
  provideHeaderHeights();
  const navbar = document.querySelector("#navbarMainContent");
  // Disable shadow entirely on pages with .project-hero element
  const hasProjectHero = document.querySelector(".project-hero") !== null;

  if (navbar) {
    initNavHover();
    initMenuOpenBodyClass(navbar);
    initToggleNavOnScroll();
    initMobileMenuActiveDropdown(navbar);
    initMobileAccordion(navbar);
    initOverlayToggle(navbar);
    initCaretStateSync(navbar);

    /* --- Hero shadow toggle --- */
    if (!hasProjectHero) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.target.classList.contains("hero")) {
              if (entry.intersectionRatio > 0) {
                // Navbar overlapping hero – remove shadow
                navbar.closest("nav").classList.add("no-shadow");
              } else {
                navbar.closest("nav").classList.remove("no-shadow");
              }
            }
          });
        },
        {
          rootMargin: `-${navbar.offsetHeight}px 0px 0px 0px`,
          threshold: 0,
        }
      );

      document
        .querySelectorAll(".hero")
        .forEach((hero) => observer.observe(hero));
    } else {
      // Always hide shadow when project hero present
      navbar.closest("nav").classList.add("no-shadow");
    }
  }
}
